// Home — top bar (profile + wordmark + LIVE pill) + two giant CTAs (SOLO / SQUAD)
// + LIVE NOW friends pill above the global bottom nav.
// Ported pixel-for-pixel from /tmp/new-canon screenshot + chat decisions:
//   - drop pink dot (wordmark wobbles on mount + tap)
//   - two giant SOLO/SQUAD cards (whole card is tap target)
//   - drop sponsor strip
//   - friends-live pill card (.flc-card) sits between SQUAD and BottomNav
//   - BottomNav is global to Home now — earlier "no nav" call reversed after
//     canon review (Start / Live / Recap / You is the canonical four).
// Active-night users are redirected straight to /night/$id — home is only
// shown to users with no in-progress night.

import { Link, createFileRoute, useNavigate } from '@tanstack/react-router'
import { useEffect, useState } from 'react'
import {
  type ActiveStumbleDto,
  type CurrentUser,
  type NightDetailsDto,
  authApi,
  meApi,
  nightApi,
  stumblingApi,
} from '../api'
import { CtaCard } from '../components/CtaCard'
import { Icon } from '../components/Icon'
import { LiveNowPill } from '../components/LiveNowPill'
import { Wordmark } from '../components/Wordmark'
import { track } from '../lib/analytics'
import { ONBOARDING_KEY } from './onboarding'

export const Route = createFileRoute('/')({
  component: HomeRoute,
})

type Status =
  | { kind: 'loading' }
  | { kind: 'anon' }
  | {
      kind: 'authed'
      user: CurrentUser
      active: NightDetailsDto | null
      friendsLive: ActiveStumbleDto[]
    }

function HomeRoute() {
  const navigate = useNavigate()
  const [state, setState] = useState<Status>({ kind: 'loading' })
  const [busy, setBusy] = useState(false)

  async function refresh() {
    const user = await authApi.me()
    if (!user) {
      setState({ kind: 'anon' })
      return
    }
    const [active, friendsLive] = await Promise.all([meApi.activeNight(), stumblingApi.live()])
    setState({ kind: 'authed', user, active, friendsLive })
  }

  // biome-ignore lint/correctness/useExhaustiveDependencies: refresh is a stable component-scoped closure; we intentionally run once on mount.
  useEffect(() => {
    void refresh()
  }, [])

  useEffect(() => {
    if (state.kind !== 'anon') return
    // First-visit gate: anon users who haven't seen onboarding go through it
    // before /login. Onboarding sets the flag + redirects to /login on finish.
    let seen = false
    try {
      seen = localStorage.getItem(ONBOARDING_KEY) === '1'
    } catch {
      seen = true // storage unavailable — don't trap the user in onboarding loop
    }
    navigate({ to: seen ? '/login' : '/onboarding' })
  }, [state.kind, navigate])

  // Active-night users skip home — go straight to the in-progress night.
  useEffect(() => {
    if (state.kind === 'authed' && state.active) {
      navigate({ to: '/night/$id', params: { id: state.active.night.id } })
    }
  }, [state, navigate])

  async function handleSolo() {
    if (state.kind !== 'authed') return
    if (state.active) {
      navigate({ to: '/night/$id', params: { id: state.active.night.id } })
      return
    }
    setBusy(true)
    try {
      const res = await nightApi.start()
      track('night_started', { mode: 'solo', nightId: res.night.night.id })
      navigate({ to: '/night/$id/venue', params: { id: res.night.night.id } })
    } catch {
      void refresh()
    } finally {
      setBusy(false)
    }
  }

  function handleSquad() {
    // Per chat: "Home → Squad goes straight to Squad setup. One less screen."
    navigate({ to: '/squad/setup' })
  }

  if (state.kind === 'loading') {
    return (
      <div className="stumble-screen no-head" data-testid="home-loading">
        <div style={{ padding: '60px 22px', position: 'relative', zIndex: 2 }}>
          <Wordmark size={48} />
          <p className="body muted" style={{ marginTop: 10 }}>
            loading…
          </p>
        </div>
      </div>
    )
  }
  if (state.kind === 'anon') return null
  // While the active-night redirect runs, render nothing to avoid a flash of the
  // SOLO/SQUAD picker for a user who's mid-night.
  if (state.active) return null

  const liveCount = state.friendsLive.length

  return (
    <div className="stumble-screen no-head" data-testid="home-screen">
      <div
        style={{
          position: 'relative',
          zIndex: 2,
          display: 'grid',
          // top bar · subtitle · SOLO · SQUAD · live-now pill
          // The two CTAs flex (1fr 1fr); the pill is auto-sized so it
          // hugs above the BottomNav without stealing card height.
          gridTemplateRows: 'auto auto 1fr 1fr auto',
          gap: 12,
          padding: '70px 16px 16px',
          paddingTop: 'calc(env(safe-area-inset-top) + 40px)',
          // BottomNav is fixed at bottom: calc(12 + var(--safe-bottom)) and is
          // ~64px tall (icon + label + 8/6 vertical padding). Reserve enough
          // space below the pill so it never collides with the nav, regardless
          // of iOS URL bar / keyboard state. Matches the .screen-body.with-nav
          // recipe in stumble-overrides.css (92px + safe-bottom).
          paddingBottom: 'calc(92px + var(--safe-bottom, env(safe-area-inset-bottom)))',
          // F4: force fit to the viewport — no scroll on home. minHeight kept
          // for short viewports, maxHeight + overflow:hidden caps growth so
          // long content (lots of live friends) clips into the LiveNowPill's
          // own internal scroll instead of growing the grid.
          minHeight: '100dvh',
          maxHeight: '100dvh',
          overflow: 'hidden',
        }}
      >
        {/* Top bar — profile · wordmark · LIVE pill */}
        <div
          style={{
            display: 'grid',
            gridTemplateColumns: '44px 1fr auto',
            alignItems: 'center',
            gap: 10,
          }}
        >
          <Link
            to="/you"
            className="icon-btn"
            aria-label="Your profile"
            data-testid="home-profile"
            style={{ width: 44, height: 44 }}
          >
            <Icon name="you" size={22} />
          </Link>

          <div style={{ textAlign: 'center', minWidth: 0 }}>
            <Wordmark size={48} />
          </div>

          {liveCount > 0 ? (
            <Link
              to="/stumbling"
              className="live-pill"
              aria-label={`See friends' stumbles — ${liveCount} live`}
              data-testid="home-live-pill"
            >
              <span className="live-pill-dot" aria-hidden />
              <span>
                {liveCount} <strong>LIVE</strong>
              </span>
            </Link>
          ) : (
            // Reserve the slot to keep the wordmark centered.
            <span style={{ width: 44, display: 'inline-block' }} aria-hidden />
          )}
        </div>

        {/* Subtitle: body + Caveat orange "tap one →" */}
        <p
          className="body"
          style={{
            maxWidth: '32ch',
            margin: '4px 0 0',
            lineHeight: 1.25,
          }}
        >
          For when "one more place" becomes three.{' '}
          <span className="hand" style={{ color: 'var(--orange)', fontSize: 22 }}>
            tap one →
          </span>
        </p>

        {/* SOLO card */}
        <CtaCard
          onClick={handleSolo}
          disabled={busy}
          color="ink"
          background="linear-gradient(95deg, rgba(246,233,39,0.92) 0%, rgba(246,233,39,0.55) 48%, rgba(0,0,0,0) 76%), url(/cards/start-card.jpg) 70% 50%/cover, var(--lime)"
          eyebrow="solo · timeline mode"
          title="stumble."
          body={
            <>
              Track stops, drinks, and
              <br />
              the story you'll half-remember.
            </>
          }
          ariaLabel="Start a solo stumble"
          testId="home-cta-solo"
        />

        {/* SQUAD card */}
        <CtaCard
          onClick={handleSquad}
          color="paper"
          background="linear-gradient(95deg, rgba(15,13,24,0.94) 0%, rgba(15,13,24,0.55) 48%, rgba(0,0,0,0) 76%), url(/cards/cheers-cooler.jpg) 75% 45%/cover, var(--ink)"
          eyebrow="squad · head-to-head"
          title="battle."
          body={<>Two teams. Claim bars. ⚔︎</>}
          ariaLabel="Start a squad stumble"
          testId="home-cta-squad"
        />

        {/* LIVE NOW friends pill — sits above the BottomNav, hidden when 0
            friends live. Renders nothing in that case (no reserved gap). */}
        <LiveNowPill stumbles={state.friendsLive} />
      </div>

      {/* inline home-cta + live-pill styles (lifted from canon HomeScreen <style>) */}
      <style>{`
        .home-cta {
          position: relative;
          display: grid;
          grid-template-columns: 1fr auto;
          align-items: center;
          padding: 18px 20px;
          border: 3px solid var(--line);
          border-radius: 22px;
          box-shadow: 0 8px 0 var(--shadow-ink);
          text-align: left;
          overflow: hidden;
          min-height: 0;
          width: 100%;
        }
        .home-cta:active { transform: translateY(4px); box-shadow: 0 4px 0 var(--shadow-ink); }
        .home-cta:disabled { opacity: 0.7; cursor: not-allowed; }
        .home-cta .cta-inner { display: grid; gap: 6px; min-width: 0; align-content: center; }
        .home-cta .cta-title {
          font-family: var(--font-display);
          font-size: 60px;
          line-height: 0.86;
          text-transform: lowercase;
          text-shadow: 0 4px 0 var(--shadow-ink);
        }
        .home-cta .cta-body {
          font-size: 14px;
          line-height: 1.25;
          max-width: 22ch;
        }
        .home-cta .cta-arrow {
          align-self: end;
          display: grid; place-items: center;
          width: 52px; height: 52px;
          border: 2.5px solid var(--ink);
          border-radius: 999px;
          font-family: var(--font-display);
          font-size: 26px;
          background: var(--paper);
          color: var(--ink);
        }

        .live-pill {
          display: inline-flex;
          align-items: center;
          gap: 6px;
          padding: 6px 12px;
          border: 2.5px solid var(--line);
          border-radius: 999px;
          background: var(--teal);
          color: var(--ink);
          font-family: var(--font-stamp);
          font-size: 12px;
          letter-spacing: 0.08em;
          text-transform: uppercase;
          box-shadow: 0 3px 0 var(--shadow-ink);
          min-height: 36px;
          white-space: nowrap;
        }
        .live-pill:active { transform: translateY(2px); box-shadow: 0 1px 0 var(--shadow-ink); }
        .live-pill strong { font-family: var(--font-stamp); font-weight: 700; }
        .live-pill-dot {
          display: inline-block;
          width: 8px; height: 8px;
          border-radius: 999px;
          background: var(--ink);
          box-shadow: 0 0 0 0 rgba(15, 13, 24, 0.6);
          animation: live-pulse 1.6s ease-out infinite;
        }
        @keyframes live-pulse {
          0%   { box-shadow: 0 0 0 0 rgba(15, 13, 24, 0.55); }
          70%  { box-shadow: 0 0 0 8px rgba(15, 13, 24, 0); }
          100% { box-shadow: 0 0 0 0 rgba(15, 13, 24, 0); }
        }
        @media (prefers-reduced-motion: reduce) {
          .live-pill-dot { animation: none; }
        }
      `}</style>
    </div>
  )
}
